// =====================================================================
// src/screens/v2/MyOrders.jsx
//
// Customer-side order history screen. Mounted at ?v2=my-orders.
//
// Design 2 — split into two tabs:
//   Current — placed | confirmed | preparing | ready  (action-needed)
//   Past    — completed | cancelled                   (history)
//
// Data flow:
//   App.jsx already maintains a /orders subscription for the V1
//   BusinessConnect tab (customerId == auth.uid, latest 50). MyOrders
//   reuses that data via props rather than opening a parallel
//   subscription — keeps Firestore read count low.
//
// Each row links to OrderDetail (the existing customer-flavored
// status hero + items + summary). No write actions here; this is a
// read-only history surface.
// =====================================================================

import React, { useState, useMemo } from 'react';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import BusinessLogo from '../../components/BusinessLogo';
import './MyOrders.css';

const STATUS_LABEL = {
  placed: 'Waiting for business',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// Visual class per status — drives pill color. Soft palette keeps the
// list scannable without screaming at the user.
const STATUS_TONE = {
  placed: 'pending',
  confirmed: 'active',
  preparing: 'active',
  ready: 'active',
  out_for_delivery: 'active',
  delivered: 'done',
  completed: 'done',
  cancelled: 'cancelled',
};

// Statuses that count as "current" (action-needed) vs "past" (history).
// Anything not in CURRENT_STATUSES falls into the Past tab — keeps the
// classification forward-compatible with any new statuses added later.
const CURRENT_STATUSES = new Set([
  'placed', 'confirmed', 'preparing', 'ready', 'out_for_delivery',
]);

// Relative-time helper. Firestore Timestamps come through with a
// toMillis() method; older serialised docs might have a plain Date or
// ISO string, so the helper accepts whatever's there.
function relativeTime(ts) {
  if (!ts) return '';
  let ms;
  if (typeof ts.toMillis === 'function') ms = ts.toMillis();
  else if (ts instanceof Date) ms = ts.getTime();
  else if (typeof ts === 'string') ms = new Date(ts).getTime();
  else if (typeof ts === 'number') ms = ts;
  else return '';
  if (!ms) return '';
  const diff = Date.now() - ms;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1)   return 'just now';
  if (minutes < 60)  return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)    return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7)      return `${days}d ago`;
  if (days < 30)     return `${Math.floor(days / 7)}w ago`;
  if (days < 365)    return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function MyOrders({
  onBack,
  onOpenOrderDetail,
  // Data plumbed down from App.jsx (already subscribed for the V1
  // BusinessConnect tab). Avoids opening a parallel subscription.
  businessConnects = [],
  businessConnectsLoaded = false,
  businessCache = {},
}) {
  const [tab, setTab] = useState('current');

  // Partition orders into current vs past. Done once per render via
  // useMemo since businessConnects can update on snapshot tick.
  const { currentOrders, pastOrders } = useMemo(() => {
    const current = [];
    const past = [];
    for (const o of businessConnects) {
      if (CURRENT_STATUSES.has(o.status)) current.push(o);
      else past.push(o);
    }
    return { currentOrders: current, pastOrders: past };
  }, [businessConnects]);

  const list = tab === 'current' ? currentOrders : pastOrders;

  return (
    <div className="v2mo-root">
      {/* ============ TOP BAR ============ */}
      <div className="v2mo-top-bar">
        <button className="v2mo-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="v2mo-title-wrap">
          <h1 className="v2mo-title">My Orders</h1>
          <p className="v2mo-subtitle">
            {businessConnectsLoaded
              ? `${currentOrders.length} active · ${pastOrders.length} past`
              : 'Loading…'}
          </p>
        </div>
      </div>

      {/* ============ TABS ============ */}
      <div className="v2mo-tabs">
        <button
          className={`v2mo-tab${tab === 'current' ? ' v2mo-tab--active' : ''}`}
          onClick={() => setTab('current')}
        >
          Current
          {currentOrders.length > 0 && (
            <span className="v2mo-tab-count">{currentOrders.length}</span>
          )}
        </button>
        <button
          className={`v2mo-tab${tab === 'past' ? ' v2mo-tab--active' : ''}`}
          onClick={() => setTab('past')}
        >
          Past
          {pastOrders.length > 0 && (
            <span className="v2mo-tab-count">{pastOrders.length}</span>
          )}
        </button>
      </div>

      {/* ============ LIST ============ */}
      <div className="v2mo-list">
        {!businessConnectsLoaded ? (
          <div className="v2mo-empty">
            <p className="v2mo-empty-title">Loading…</p>
          </div>
        ) : list.length === 0 ? (
          <div className="v2mo-empty">
            <div className="v2mo-empty-icon">
              <ShoppingBag size={22} />
            </div>
            <p className="v2mo-empty-title">
              {tab === 'current' ? 'No active orders' : 'No past orders'}
            </p>
            <p className="v2mo-empty-sub">
              {tab === 'current'
                ? 'Orders you place will show up here while they\u2019re in progress.'
                : 'Completed and cancelled orders will show up here.'}
            </p>
          </div>
        ) : (
          list.map((o) => {
            const biz = businessCache[o.businessId] || {};
            const bizName = biz.name || 'Loading…';
            const bizLogo = biz.logo || '🏪';
            // Phase 2.3 C2.5 — uploaded logo URL (added to businessCache
            // projection in App.jsx). null falls back to bizLogo emoji.
            const bizLogoImageUrl = biz.logoImageUrl || null;
            const status = o.status || 'placed';
            const tone = STATUS_TONE[status] || 'pending';
            const orderRef = o.displayNumber || o.id;
            return (
              <button
                key={o.id}
                className="v2mo-row"
                onClick={() => onOpenOrderDetail?.(o.id)}
              >
                <BusinessLogo
                  className="v2mo-row-logo"
                  imageUrl={bizLogoImageUrl}
                  emoji={bizLogo}
                  bg={undefined}
                />
                <div className="v2mo-row-body">
                  <div className="v2mo-row-top">
                    <span className="v2mo-row-biz">{bizName}</span>
                    <span className="v2mo-row-time">{relativeTime(o.placedAt)}</span>
                  </div>
                  <div className="v2mo-row-bottom">
                    <span className={`v2mo-pill v2mo-pill--${tone}`}>
                      {STATUS_LABEL[status] || status}
                    </span>
                    <span className="v2mo-row-meta">
                      #{orderRef}
                      {typeof o.total === 'number' && o.total > 0 && (
                        <> · ₹{o.total.toLocaleString('en-IN')}</>
                      )}
                    </span>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
